'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useMemo } from 'react'

/**
 * FRIDGE-1607 — search suggestions load the page the shopper is about to open,
 * and only that one.
 *
 * A suggestion rendered as a plain <Link> prefetches its page as soon as it
 * appears. Measured on production 10/16/2026 (20 searches, 1280 px): the
 * homepage search box made 75 background page loads (6.4 MB), and the page's
 * heap grew 15.1%. The header search, whose suggestions stopped prefetching in
 * FRIDGE-1509, made none and grew 5.5%, but the chosen page then took 1,347 ms
 * from click to heading against 194 ms in the homepage box.
 *
 * So the suggestion links stay `prefetch={false}`, and this prefetches on
 * intent, the pattern Next.js documents for long link lists
 * (node_modules/next/dist/docs/01-app/02-guides/prefetching.md, "Manual
 * prefetch" with `router.prefetch`): when the pointer rests on a suggestion for
 * INTENT_DWELL_MS (sweeping across the list loads nothing), when the arrow keys
 * highlight it, when it takes keyboard focus, or when a finger touches it. Each
 * page is prefetched at most once per box. Offer links (`/r/…`, the affiliate
 * click record) and anything off the site are never prefetched. Development
 * builds do not prefetch at all, so this is measured on a production build.
 */

/** How long the pointer rests on a suggestion before its page is prefetched. */
export const INTENT_DWELL_MS = 80

/** A same-site page path that is not an offer link. */
export function canIntentPrefetch(href: string | null | undefined): href is string {
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/r/')
}

export interface IntentPrefetcher {
  /** Prefetch now: an arrow-key highlight, keyboard focus, a touch. */
  prefetchNow: (href: string | null | undefined) => void
  /** Prefetch once the pointer has rested on the suggestion for INTENT_DWELL_MS. */
  prefetchOnRest: (href: string | null | undefined) => void
  /** The pointer left before the rest was long enough. */
  cancelRest: () => void
}

/** The timer pair the prefetcher waits with. */
export interface IntentTimers {
  set: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>
  clear: (handle: ReturnType<typeof setTimeout>) => void
}

/**
 * The page's own timers, called as plain functions. Chrome refuses
 * `setTimeout` called as another object's method ("Illegal invocation"):
 * `{ set: setTimeout }.set(…)` threw on every pointer rest on the pull
 * request 812 preview (10/17/2026), so no suggestion was ever prefetched on
 * hover. Node does not care, which is why the unit tests passed.
 */
const PAGE_TIMERS: IntentTimers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (handle) => clearTimeout(handle),
}

/** The logic, apart from React: `prefetch` is the router's, the timers are injectable for tests. */
export function createIntentPrefetcher(
  prefetch: (href: string) => void,
  timers: IntentTimers = PAGE_TIMERS,
): IntentPrefetcher {
  const done = new Set<string>()
  let pending: ReturnType<typeof setTimeout> | null = null
  const cancelRest = () => {
    if (pending !== null) {
      timers.clear(pending)
      pending = null
    }
  }
  const prefetchNow = (href: string | null | undefined) => {
    if (!canIntentPrefetch(href) || done.has(href)) return
    done.add(href)
    prefetch(href)
  }
  const prefetchOnRest = (href: string | null | undefined) => {
    cancelRest()
    if (!canIntentPrefetch(href) || done.has(href)) return
    pending = timers.set(() => {
      pending = null
      prefetchNow(href)
    }, INTENT_DWELL_MS)
  }
  return { prefetchNow, prefetchOnRest, cancelRest }
}

export function useIntentPrefetch(): IntentPrefetcher {
  const router = useRouter()
  const prefetcher = useMemo(() => createIntentPrefetcher((href) => router.prefetch(href)), [router])
  useEffect(() => prefetcher.cancelRest, [prefetcher])
  return prefetcher
}
